import { ChevronLeft, ChevronRight } from 'react-feather';

import Button from '@/shared/components/UI/Button';
import { useQueryParams } from '@/shared/hooks/useQueryParams';

const LIMIT = 20; // 한 페이지당 아이템 수 (원하는 값으로 변경)

interface PaginationProps {
  totalCount: number;
}

const Pagination = ({ totalCount }: PaginationProps) => {
  const { getParam, getAllParams, setAllParams } = useQueryParams();
  const currentPage = Number(getParam('page', 1));

  // 페이지 이동 함수
  const goToPage = (page: number) => {
    if (page < 1 || page > totalCount) return;

    const currentParams = getAllParams(
      ['page', 'limit', 'stockStatus', 'sort'],
      {
        page: '1',
        limit: String(LIMIT),
        stockStatus: 'all',
        sort: 'latest',
      },
    );

    setAllParams(
      {
        page: String(page),
        limit: String(LIMIT),
        stockStatus: currentParams.stockStatus || 'all',
        sort: currentParams.sort || 'latest',
      },
      { resetPage: false },
    );
  };

  // 페이지네이션 버튼 목록 생성
  const getPageButtons = () => {
    const maxButtons = 8;
    const pages: (number | string)[] = [];

    if (totalCount <= maxButtons) {
      for (let i = 1; i <= totalCount; i++) pages.push(i);
    } else if (currentPage <= 4) {
      // 앞쪽에 몰려있을 때
      for (let i = 1; i <= 6; i++) pages.push(i);
      pages.push('...');
      pages.push(totalCount);
    } else if (currentPage >= totalCount - 3) {
      // 뒤쪽에 몰려있을 때
      pages.push(1);
      pages.push('...');
      for (let i = totalCount - 5; i <= totalCount; i++) pages.push(i);
    } else {
      // 중간에 있을 때
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - 2; i <= currentPage + 2; i++) pages.push(i);
      pages.push('...');
      pages.push(totalCount);
    }
    return pages;
  };

  return (
    <div className="mx-auto my-8 w-full max-w-[50vw] text-center">
      <ul className="mt-5 flex list-none items-center justify-center p-0">
        <li className="flex items-center justify-center text-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="이전 페이지"
            className="mr-4 h-6 w-6 rounded-full pr-0.5 hover:bg-[#95699f] disabled:opacity-100 disabled:hover:bg-transparent"
          >
            <ChevronLeft
              className={`text-[#666] ${currentPage === 1 ? 'opacity-50' : ''}`}
            />
          </Button>
        </li>
        <div className="flex gap-1">
          {getPageButtons().map((item, idx) => {
            if (item === '...') {
              return (
                <li
                  // eslint-disable-next-line react/no-array-index-key
                  key={`ellipsis-${idx}`}
                  className="flex items-center justify-center"
                >
                  <span className="px-2 text-[14px] text-[#666]">...</span>
                </li>
              );
            }
            const page = item as number;
            return (
              <li key={page}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => goToPage(page)}
                  disabled={currentPage === page}
                  aria-current={currentPage === page ? 'page' : undefined}
                  className={`flex h-6 w-6 items-center justify-center rounded-full p-0 text-[14px] font-normal leading-none disabled:opacity-100 ${
                    currentPage === page
                      ? 'cursor-default bg-[#95699f] text-[#fff]'
                      : 'text-[#666] hover:bg-[#95699f] hover:text-white'
                  }`}
                >
                  {page}
                </Button>
              </li>
            );
          })}
        </div>
        <li className="flex items-center justify-center text-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalCount}
            aria-label="다음 페이지"
            className="ml-4 h-6 w-6 rounded-full bg-transparent hover:bg-[#95699f] disabled:opacity-100 disabled:hover:bg-transparent"
          >
            <ChevronRight
              className={`text-[#666] ${currentPage === totalCount ? 'opacity-50' : ''}`}
            />
          </Button>
        </li>
      </ul>
    </div>
  );
};

export default Pagination;
